import React, { ChangeEvent, ClipboardEvent, useCallback, useMemo, useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { useForm, z, zodResolver } from '@fiverr-private/forms';
import { Link } from '@fiverr-private/typography';
import { stats } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { raiseToast } from '@fiverr-private/toast';
import { TranslateOptionsWithTemplatesParams } from '@fiverr-private/i18n-react';
import { useBriefCreationContext } from '../context/BriefCreationContext';
import { appTranslate, GenerationError, InvalidIntent } from '../i18n';
import { BriefClassificationResponse, IntentClassification, LeafClassification } from '../api';
import { getUserIsActive } from '../api/buyerData';
import { METRICS_PREFIX, Step } from '../constants';
import { briefCreationClickAction, briefInputPastedAction } from '../monitoring';
import { logger } from '../utils/logger';
import { useClassifyUserInput, useHandleDisambiguation } from './classification';
import { useExtractAndGenerateBrief } from './useExtractAndGenerateBrief';
import { usePopulateBuyerType } from './classification/usePopulateBuyerType';
import { isInvalidIntent } from './classification/utils';

export const MAX_INPUT_LENGTH = 2000;
export const TEXTAREA_COUNTER_THRESHOLD = 1800;

const mapErrorToLink: Record<InvalidIntent, string | { tos: string; cs: string }> = {
  non_english: '',
  non_service: '',
  insufficient_information: '',
  contact_support: pathfinder('help_center_homepage'),
  seller_intent: pathfinder('help_center_section', {
    section_id: '17142926163729-Become-a-seller',
  }),
  tos_violation: {
    tos: pathfinder('legal_portal_article_page', {
      category: 'legal-terms',
      page_slug: 'terms-of-service',
    }),
    cs: pathfinder('community_standards', { slug: 'integrity-authenticity' }),
  },
};

const getErrorTemplate = (errorMessageKey: GenerationError) => {
  const educationLink = mapErrorToLink[errorMessageKey];

  if (typeof educationLink === 'string') {
    return {
      link: (text) => (
        <Link color="red_1000" href={educationLink}>
          {text}
        </Link>
      ),
    };
  }

  return Object.keys(educationLink).reduce((acc, key) => {
    acc[key] = (text) => (
      <Link color="red_1000" href={educationLink[key]}>
        {text}
      </Link>
    );
    return acc;
  }, {});
};

const isIntent = (str: string): str is IntentClassification =>
  (Object.keys(mapErrorToLink) as IntentClassification[]).includes(str as IntentClassification);

export const useBriefForm = () => {
  const {
    contextId,
    setIsOpen,
    placeholder,
    userInput,
    setUserInput,
    source,
    setStep,
    parentElement,
    rollouts,
    variant,
  } = useBriefCreationContext();
  const { handleClassifyUserInput, isLoading: isLoadingClassification } = useClassifyUserInput();
  const { handleDisambiguationStep } = useHandleDisambiguation();
  const { extractAndGenerateBrief, isLoading: isLoadingGenerate } = useExtractAndGenerateBrief();
  const { populateBuyerType, isBuyerTypeLoading } = usePopulateBuyerType();
  const [pastedInput, setPastedInput] = useState<string | null>(null);

  const abortController = new AbortController();

  const schema = z.object({
    leaf_category_id: z.string().optional(),
    prompt: z
      .string()
      .min(10, appTranslate('generation_errors.minimum_10_characters') as string)
      .refine(
        (value) => {
          const words = value.trim().split(/\s+/);
          return words.length >= 5;
        },
        {
          message: appTranslate('generation_errors.minimum_5_words') as string,
        }
      ),
  });

  const methods = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      prompt: userInput,
    },
  });

  useEffect(() => {
    methods.setValue('prompt', userInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInput]);

  const handleIntentClassificationError = (intentClassification?: IntentClassification) => {
    if (!intentClassification) {
      return;
    }

    let errorMessage = '';

    if (isIntent(intentClassification)) {
      const errorMessageKey: GenerationError = intentClassification as unknown as GenerationError;
      if (errorMessageKey === 'insufficient_information') {
        setStep(Step.Rephrase);
        setIsOpen(true);
        return;
      }

      errorMessage = appTranslate(`generation_errors.${errorMessageKey}`, {
        templates: getErrorTemplate(errorMessageKey),
      } as TranslateOptionsWithTemplatesParams);
    } else {
      errorMessage = appTranslate('generation_errors.insufficient_information');
    }

    methods.setError('prompt', {
      type: 'manual',
      message: errorMessage as any as string,
    });
    stats.count(`${METRICS_PREFIX}.classify_user_input`, 'invalid_intent');
    logger.warn(new Error('Invalid intent'), { intentClassification, userInput });
    return errorMessage;
  };

  const validateUserNotRestricted = async () => {
    try {
      const { data: isActive } = await getUserIsActive();

      if (!isActive) {
        raiseToast({
          type: 'error',
          location: 'top',
          message: appTranslate('restricted_account_error', {
            templates: {
              bold: (text) => <b>{text}</b>,
              link: (text) => (
                <Link fontStyle="italic" href={pathfinder('help_center_homepage')}>
                  {text}
                </Link>
              ),
            },
          }),
        });

        return false;
      }
      return true;
    } catch (error) {
      logger.error(new Error('An error occurred'), { cause: error });

      raiseToast({
        type: 'error',
        location: 'top',
        message: appTranslate('something_went_wrong'),
      });
      return false;
    }
  };

  const debouncedValidate = useMemo(
    () =>
      debounce(async () => {
        await methods.trigger('prompt');
      }, 1500),
    [methods]
  );

  const setExceedsLimitError = useCallback(() => {
    debouncedValidate.cancel();
    methods.setError('prompt', {
      type: 'manual',
      message: appTranslate('generation_errors.exceeds_character_limit') as string,
    });
  }, [debouncedValidate, methods]);

  const onTextareaChange = async (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setUserInput(value);
    if (pastedInput && value.length === MAX_INPUT_LENGTH && value.length - userInput.length < pastedInput.length) {
      setPastedInput(null);
      setExceedsLimitError();
    } else if (methods.formState.errors.prompt) {
      await debouncedValidate.flush();
      await methods.trigger();
    } else {
      await debouncedValidate();
    }
  };

  const handleContentPaste = async (e: ClipboardEvent) => {
    const pastedInput = e.clipboardData?.getData('text');
    setPastedInput(pastedInput);
    briefInputPastedAction(contextId, pastedInput.length);
  };

  const onSubmit = async () =>
    methods.handleSubmit(async (data) => {
      await debouncedValidate.flush();
      if (!data?.prompt) {
        return;
      }

      let classification: BriefClassificationResponse | undefined;
      let leafClassification: LeafClassification | undefined;
      let extractedData: BriefClassificationResponse | undefined;

      const isDisambiguation = data.leaf_category_id;

      briefCreationClickAction({
        step: isDisambiguation ? Step.Disambiguation : Step.Creation,
        action: 'generate_brief',
        input: isDisambiguation ? data.leaf_category_id : data.prompt,
        briefContextId: contextId,
        eventSource: source || '',
        parentElement,
        variant,
      });

      if (isDisambiguation) {
        ({ selectedLeafClassification: leafClassification, extractedData } =
          (await handleDisambiguationStep({
            leafCategoryId: data.leaf_category_id,
            userInput: data.prompt,
            abortController,
          })) || {});
      } else {
        const isValid = await validateUserNotRestricted();
        if (!isValid) {
          return;
        }
        // make call to define rnc status in parallel
        const [classifyResult] = await Promise.allSettled([
          handleClassifyUserInput({
            userInput: data.prompt,
            abortController,
          }),
          populateBuyerType(abortController),
        ]);

        if (classifyResult.status === 'rejected') {
          raiseToast({
            type: 'error',
            location: 'top',
            message: appTranslate('something_went_wrong'),
          });
          return;
        }

        let intentClassificationResult: IntentClassification | undefined;
        ({ leafClassification, classification, intentClassificationResult } = classifyResult.value);

        if (isInvalidIntent(intentClassificationResult)) {
          handleIntentClassificationError(intentClassificationResult);
          return;
        }
      }

      if (!leafClassification) {
        return;
      }

      const subcategoryId = leafClassification.sub_category_id;
      if (!subcategoryId) {
        logger.warn(new Error('Failed to classify subcategory id for input'), { input: data?.prompt });
        return;
      }

      setIsOpen(true);

      await extractAndGenerateBrief({
        extractedData,
        leafClassification,
        userInput: data.prompt,
        classification,
        abortController,
        rollouts,
      });
    })();

  const handlePromptKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (userInput.length === MAX_INPUT_LENGTH && !['Backspace', 'Delete'].includes(event.key)) {
      setExceedsLimitError();
    }
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  };

  return {
    variant,
    methods,
    onTextareaChange,
    onSubmit,
    isLoading: isLoadingClassification || isLoadingGenerate || isBuyerTypeLoading,
    placeholder,
    handleOnKeyDown,
    handleContentPaste,
    handlePromptKeyDown,
  };
};
